<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">加好友歡迎詞</h1>
      </div>
    </div>

    <div class="page-container welcome-page">
      <p class="welcome-lead">
        從已經設定好的訊息範本挑選 1 到 5 則。第一次加入，或封鎖後再解除封鎖，會依序一次收到這些訊息。仍是好友的人不會再收到。未挑選則不發送。
      </p>

      <div v-if="loading" class="welcome-empty">載入中...</div>
      <div v-else-if="configs.length === 0" class="welcome-empty">
        還沒有 LINE 官方帳號。
        <router-link :to="{ name: 'ClientLineConfigList' }">前往建立</router-link>
      </div>
      <div v-else class="welcome-list">
        <button type="button" class="welcome-preview-toggle" @click="mobilePreviewOpen = !mobilePreviewOpen">
          <i :class="mobilePreviewOpen ? 'ri-eye-off-line' : 'ri-smartphone-line'"></i>
          {{ mobilePreviewOpen ? '關閉預覽' : '看預覽' }}
        </button>
        <div v-if="mobilePreviewOpen" class="welcome-preview-mobile">
          <LineMessagePreview
            :messages="previewMessages"
            empty-text="挑選訊息範本後在這裡預覽"
          />
        </div>
        <section
          v-for="item in configs"
          :key="item.lineConfigId"
          class="welcome-card"
          :class="{ active: item.lineConfigId === activeLineConfigId }"
          @click="activeLineConfigId = item.lineConfigId"
        >
          <header class="welcome-card-header">
            <h2>{{ item.configName || '未命名官方帳號' }}</h2>
            <span v-if="item.botBasicId" class="welcome-basic-id">{{ item.botBasicId }}</span>
          </header>

          <p class="welcome-label">已挑選（{{ (drafts[item.lineConfigId] || []).length }}/5）</p>
          <ol v-if="(drafts[item.lineConfigId] || []).length" class="welcome-selected">
            <li v-for="(templateId, index) in drafts[item.lineConfigId]" :key="templateId">
              <span class="welcome-selected-order">{{ index + 1 }}</span>
              <span class="welcome-selected-body">
                <span class="welcome-selected-name">{{ templateOf(item, templateId).name }}</span>
                <span class="welcome-selected-meta">{{ typeLabel(templateOf(item, templateId).messageType) }}</span>
              </span>
              <span class="welcome-selected-actions">
                <button type="button" class="welcome-icon-btn" :disabled="index === 0" title="上移" @click="move(item, index, -1)">
                  <i class="ri-arrow-up-s-line"></i>
                </button>
                <button
                  type="button"
                  class="welcome-icon-btn"
                  :disabled="index === drafts[item.lineConfigId].length - 1"
                  title="下移"
                  @click="move(item, index, 1)"
                >
                  <i class="ri-arrow-down-s-line"></i>
                </button>
                <button type="button" class="welcome-icon-btn" title="移除" @click="remove(item, templateId)">
                  <i class="ri-close-line"></i>
                </button>
              </span>
            </li>
          </ol>
          <p v-else class="welcome-empty">尚未挑選，加入好友時不會發送歡迎詞。</p>

          <div class="welcome-picker">
            <p class="welcome-label">挑選範本</p>
            <LineMsgTemplatePicker
              :model-value="null"
              :exclude-ids="drafts[item.lineConfigId] || []"
              :disabled="(drafts[item.lineConfigId] || []).length >= 5"
              @update:model-value="(template) => add(item, template)"
              @loaded="rememberTemplates"
            />
            <p v-if="(drafts[item.lineConfigId] || []).length >= 5" class="welcome-hint">已挑選 5 則，若要更換請先移除。</p>
            <div class="picker-goto-link">
              <router-link :to="{ name: 'LineMsgTemplateCreate' }" target="_blank">
                <i class="ri-add-line"></i> 新增 LINE 訊息範本
              </router-link>
              <router-link :to="{ name: 'LineMsgTemplates' }">前往訊息範本</router-link>
            </div>
          </div>

          <div class="welcome-actions">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="savingId === item.lineConfigId || !isDirty(item)"
              @click="save(item)"
            >
              {{ savingId === item.lineConfigId ? '儲存中...' : '儲存' }}
            </button>
            <button
              v-if="(drafts[item.lineConfigId] || []).length"
              type="button"
              class="btn btn-secondary"
              @click="clear(item)"
            >
              清空，不發送
            </button>
          </div>
        </section>
      </div>

      <aside v-if="!loading && configs.length" class="welcome-preview-dock">
        <div class="welcome-preview-sticky">
          <div class="welcome-preview-title">
            <i class="ri-smartphone-line"></i>
            <span>{{ activeItem ? (activeItem.configName || '未命名官方帳號') : '歡迎詞' }}</span>
            <span v-if="previewMessages.length > 1" class="welcome-preview-count">共 {{ previewMessages.length }} 則</span>
          </div>
          <LineMessagePreview
            :messages="previewMessages"
            empty-text="挑選訊息範本後在這裡預覽"
          />
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { lineWelcomeService } from '@/api/modules/lineWelcome.js';
import { showError, showSuccess } from '@/utils/swal.js';
import LineMessagePreview from '@/components/LineMessagePreview.vue';
import LineMsgTemplatePicker from '@/components/LineMsgTemplatePicker.vue';
import { toLineMsgTemplatePreviewData, MSG_TYPE_LABEL } from '@/utils/lineMsgTemplatePreview.js';

export default {
  name: 'LineWelcomeMessage',
  components: { LineMessagePreview, LineMsgTemplatePicker },
  data() {
    return {
      loading: false,
      savingId: null,
      configs: [],
      templatesById: {},
      drafts: {},
      activeLineConfigId: '',
      mobilePreviewOpen: false
    };
  },
  computed: {
    activeItem() {
      return this.configs.find((item) => item.lineConfigId === this.activeLineConfigId) || this.configs[0] || null;
    },
    previewMessages() {
      const item = this.activeItem;
      if (!item) return [];
      return (this.drafts[item.lineConfigId] || [])
        .map((templateId) => toLineMsgTemplatePreviewData(this.templateOf(item, templateId)))
        .filter((message) => message.messageType);
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const welcomeRes = await lineWelcomeService.get();
        const data = welcomeRes.data || {};
        this.configs = data.configs || [];
        const drafts = {};
        for (const item of this.configs) {
          drafts[item.lineConfigId] = [...(item.templateIds || [])];
        }
        this.drafts = drafts;
        if (!this.activeLineConfigId && this.configs[0]) {
          this.activeLineConfigId = this.configs[0].lineConfigId;
        }
      } catch (error) {
        showError(error.response?.data?.message || error.message || '載入失敗');
      } finally {
        this.loading = false;
      }
    },
    rememberTemplates(list) {
      if (!Array.isArray(list) || !list.length) return;
      const next = { ...this.templatesById };
      for (const template of list) {
        if (template?.id) next[template.id] = template;
      }
      this.templatesById = next;
    },
    templateOf(item, templateId) {
      return this.templatesById[templateId]
        || (item.templates || []).find((template) => template.id === templateId)
        || { id: templateId, name: '已刪除的訊息範本', messageType: null };
    },
    typeLabel(messageType) {
      return MSG_TYPE_LABEL[messageType] || '';
    },
    add(item, template) {
      const templateId = template?.id;
      if (!templateId) return;
      this.rememberTemplates([template]);
      this.activeLineConfigId = item.lineConfigId;
      const current = this.drafts[item.lineConfigId] || [];
      if (current.length >= 5 || current.includes(templateId)) return;
      this.drafts[item.lineConfigId] = [...current, templateId];
    },
    remove(item, templateId) {
      this.drafts[item.lineConfigId] = (this.drafts[item.lineConfigId] || []).filter((id) => id !== templateId);
    },
    move(item, index, offset) {
      const next = [...(this.drafts[item.lineConfigId] || [])];
      const target = index + offset;
      if (target < 0 || target >= next.length) return;
      const [row] = next.splice(index, 1);
      next.splice(target, 0, row);
      this.drafts[item.lineConfigId] = next;
    },
    clear(item) {
      this.drafts[item.lineConfigId] = [];
    },
    isDirty(item) {
      const saved = item.templateIds || [];
      const draft = this.drafts[item.lineConfigId] || [];
      if (saved.length !== draft.length) return true;
      return saved.some((id, index) => id !== draft[index]);
    },
    async save(item) {
      const templateIds = this.drafts[item.lineConfigId] || [];
      this.savingId = item.lineConfigId;
      try {
        const res = await lineWelcomeService.save(item.lineConfigId, templateIds);
        const saved = res.data;
        if (saved) {
          const index = this.configs.findIndex((row) => row.lineConfigId === item.lineConfigId);
          if (index >= 0) this.configs.splice(index, 1, saved);
          this.drafts[item.lineConfigId] = [...(saved.templateIds || [])];
        }
        showSuccess(templateIds.length ? '已儲存歡迎詞' : '已改為不發送歡迎詞');
      } catch (error) {
        showError(error.response?.data?.message || error.message || '儲存失敗');
      } finally {
        this.savingId = null;
      }
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.welcome-lead {
  margin: 0 0 8px;
  color: #4b5563;
  line-height: 1.6;
}

.welcome-page {
  margin-right: 360px;
}

.welcome-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
}

.welcome-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
}

.welcome-card.active {
  border-color: #93c5fd;
}

.welcome-preview-dock {
  position: fixed;
  top: 110px;
  right: 28px;
  z-index: 90;
  width: 344px;
}

.welcome-preview-sticky {
  max-height: calc(100vh - 130px);
  overflow: auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  padding: 12px 12px 16px;
}

.welcome-preview-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
}

.welcome-preview-count {
  margin-left: auto;
  color: #2563eb;
  font-weight: 500;
}

.welcome-preview-toggle,
.welcome-preview-mobile {
  display: none;
}

.welcome-card-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 16px;
}

.welcome-card-header h2 {
  margin: 0;
  font-size: 18px;
}

.welcome-basic-id {
  color: #6b7280;
  font-size: 13px;
}

.welcome-label {
  margin: 0 0 8px;
  font-weight: 600;
}

.welcome-selected {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.welcome-selected li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #eff6ff;
}

.welcome-selected-order {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #2563eb;
  color: #fff;
  font-size: 12px;
  line-height: 22px;
  text-align: center;
  flex: none;
}

.welcome-selected-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.welcome-selected-name {
  font-weight: 600;
}

.welcome-selected-meta {
  color: #6b7280;
  font-size: 12px;
}

.welcome-selected-actions {
  display: flex;
  gap: 4px;
}

.welcome-icon-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #374151;
}

.welcome-icon-btn:disabled {
  opacity: 0.4;
}

.welcome-picker {
  margin-top: 8px;
}

.welcome-hint {
  margin: 8px 0 0;
  color: #6b7280;
  font-size: 13px;
}

.picker-goto-link {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
  font-size: 0.9rem;
}

.picker-goto-link a {
  color: #2563eb;
  text-decoration: none;
}

.picker-goto-link a:hover {
  text-decoration: underline;
}

.welcome-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.welcome-empty {
  margin-top: 8px;
  color: #6b7280;
}

@media (max-width: 900px) {
  .welcome-page {
    margin-right: 0;
  }

  .welcome-preview-dock {
    display: none;
  }

  .welcome-preview-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-end;
    padding: 7px 14px;
    border: 1px solid #06C755;
    border-radius: 20px;
    background: #fff;
    color: #06C755;
    font-size: 13px;
    font-weight: 500;
  }

  .welcome-preview-mobile {
    display: flex;
    justify-content: center;
  }
}
</style>
